import { z } from 'zod';
import { allocationUnitSchema, bookingTypeSchema } from './allocations.js';
import { addDays, weekOf } from './calendar/dates.js';
import type { HolidayRegion } from './calendar/holidays.js';
import { addWorkingDays } from './calendar/working-days.js';
import { visibilitySchema } from './permissions.js';
import { isoDateSchema, personSchema } from './projects.js';
import { timestampSchema } from './admin.js';
import { taskPrioritySchema, taskStatusRefSchema, taskTypeSchema } from './tasks.js';

/** DASH-01..06: the role-based overview (start page). */

// --- Rules ------------------------------------------------------------------------------------

export const dueGroupSchema = z.enum(['overdue', 'today', 'this_week', 'later', 'none']);
export type DueGroup = z.infer<typeof dueGroupSchema>;

/** DASH-01: group of a due date relative to today (Europe/Berlin business date). */
export function dueGroup(dueDate: string | null, today: string): DueGroup {
  if (dueDate === null) return 'none';
  if (dueDate < today) return 'overdue';
  if (dueDate === today) return 'today';
  if (dueDate <= weekOf(today).to) return 'this_week';
  return 'later';
}

/**
 * Columns of the board view of "Meine Tasks" (DASH-01, E-39), left to right; tasks without a
 * due date come first.
 */
export const myTasksBoardColumnSchema = z.enum([
  'none',
  'overdue',
  'today',
  'tomorrow',
  'rest_of_week',
  'next_week',
  'future',
]);
export type MyTasksBoardColumn = z.infer<typeof myTasksBoardColumnSchema>;

/**
 * Board column of a due date relative to today (Europe/Berlin business date, weeks Monday to
 * Sunday). "Rest der Woche" is the day after tomorrow up to Sunday – empty on Saturdays; on a
 * Sunday tomorrow is already next week's Monday and counts as "Morgen".
 */
export function myTasksBoardColumn(dueDate: string | null, today: string): MyTasksBoardColumn {
  if (dueDate === null) return 'none';
  if (dueDate < today) return 'overdue';
  if (dueDate === today) return 'today';
  if (dueDate === addDays(today, 1)) return 'tomorrow';
  const endOfWeek = weekOf(today).to;
  if (dueDate <= endOfWeek) return 'rest_of_week';
  if (dueDate <= addDays(endOfWeek, 7)) return 'next_week';
  return 'future';
}

/**
 * DASH-04: a customer comment is unanswered if it is the task's last comment and nobody answered
 * by the end of the following working day – it counts from the working day after that.
 * `commentDate` is the business date (Europe/Berlin) of the comment.
 */
export function isUnansweredSince(
  commentDate: string,
  today: string,
  region: HolidayRegion,
): boolean {
  return today > addWorkingDays(commentDate, 1, region);
}

/** Look-ahead of milestones (DASH-04) and of overbooking and placeholders (DASH-04, DASH-05). */
export const DASHBOARD_MILESTONE_DAYS = 14;
export const DASHBOARD_RESOURCE_WEEKS = 4;
/** Most tasks in "my tasks" (DASH-01); more are reported as truncated. */
export const MY_TASKS_LIMIT = 200;
export const RECENT_COMMENTS_LIMIT = 10;
/** Length of comment excerpts on the overview (DASH-06). */
export const DASHBOARD_EXCERPT_LENGTH = 160;

/** Period of the resource look-ahead: this week and the following ones. */
export function dashboardResourceRange(today: string): { from: string; to: string } {
  const from = weekOf(today).from;
  return { from, to: addDays(from, DASHBOARD_RESOURCE_WEEKS * 7 - 1) };
}

// --- Responses --------------------------------------------------------------------------------

const projectRefSchema = z.object({ id: z.uuid(), key: z.string(), name: z.string() });

/** GET /me/tasks (DASH-01). */
export const myTaskSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  title: z.string(),
  type: taskTypeSchema,
  project: projectRefSchema,
  status: taskStatusRefSchema,
  priority: taskPrioritySchema,
  dueDate: isoDateSchema.nullable(),
  group: dueGroupSchema,
});
export type MyTask = z.infer<typeof myTaskSchema>;

export const myTasksResponseSchema = z.object({
  items: z.array(myTaskSchema),
  truncated: z.boolean(),
});
export type MyTasksResponse = z.infer<typeof myTasksResponseSchema>;

/** GET /dashboard/project-health (DASH-04): projects with project.edit. */
export const projectHealthSchema = z.object({
  project: projectRefSchema,
  overdueTasks: z.number().int(),
  unassignedTasks: z.number().int(),
  milestones: z.array(z.object({ key: z.string(), title: z.string(), dueDate: isoDateSchema })),
  /** Tasks whose last comment is an unanswered customer comment, oldest first (at most 5). */
  unansweredComments: z.array(
    z.object({ taskKey: z.string(), taskTitle: z.string(), since: timestampSchema }),
  ),
  unansweredCount: z.number().int(),
  /** People overbooked in the next weeks; null without resource.view in the project. */
  overbookedPeople: z.number().int().nullable(),
});
export type ProjectHealth = z.infer<typeof projectHealthSchema>;

export const projectHealthResponseSchema = z.object({ items: z.array(projectHealthSchema) });
export type ProjectHealthResponse = z.infer<typeof projectHealthResponseSchema>;

/** GET /dashboard/resources (DASH-05, resource.view_all). */
export const resourceBottlenecksResponseSchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  overbooked: z.array(
    z.object({
      user: z.object({ id: z.uuid(), name: z.string() }),
      /** Weeks with at least one overbooked day. */
      weeks: z.number().int(),
      /** Hours beyond capacity in the period. */
      excess: z.number(),
      firstDate: isoDateSchema,
    }),
  ),
  placeholders: z.array(
    z.object({
      id: z.uuid(),
      project: projectRefSchema,
      jobRole: z.object({ id: z.uuid(), name: z.string(), color: z.string() }),
      startDate: isoDateSchema,
      endDate: isoDateSchema,
      unit: allocationUnitSchema,
      amount: z.number(),
      bookingType: bookingTypeSchema,
    }),
  ),
});
export type ResourceBottlenecksResponse = z.infer<typeof resourceBottlenecksResponseSchema>;

/** GET /dashboard/recent-comments (DASH-06). */
export const recentCommentSchema = z.object({
  id: z.uuid(),
  task: z.object({ key: z.string(), title: z.string() }),
  project: projectRefSchema,
  author: personSchema,
  excerpt: z.string(),
  visibility: visibilitySchema,
  createdAt: timestampSchema,
});
export type RecentComment = z.infer<typeof recentCommentSchema>;

export const recentCommentsResponseSchema = z.object({ items: z.array(recentCommentSchema) });
export type RecentCommentsResponse = z.infer<typeof recentCommentsResponseSchema>;
